

<template>
  <div class="chart-container">
    <!-- 顶部标题栏 -->
    <div class="header">
      <div class="left-icon">
        <!-- 模拟左侧的时钟小图标 -->
        <svg viewBox="0 0 1024 1024" width="16" height="16"><path d="M512 85.333333c-235.648 0-426.666667 191.018667-426.666667 426.666667s191.018667 426.666667 426.666667 426.666667 426.666667-191.018667 426.666667-426.666667-191.018667-426.666667-426.666667-426.666667z m0 768c-188.544 0-341.333333-152.789333-341.333333-341.333333s152.789333-341.333333 341.333333-341.333333 341.333333 152.789333 341.333333 341.333333-152.789333 341.333333-341.333333 341.333333z" fill="#00d2ff"></path><path d="M554.666667 512V256h-85.333334v298.666667l213.333334 213.333333 60.330666-60.330667L554.666667 512z" fill="#00d2ff"></path></svg>
      </div>
      <div class="right-title">
        <div class="line-view" style="background-color: #00d2ff;"/>
        每日波次批次（趟次）
      </div>
    </div>

    <!-- 图表渲染容器 -->
    <div ref="chartRef" class="echarts-box"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, shallowRef } from 'vue';
import * as echarts from 'echarts';
import { useHostAppInstance } from '@zto/zpage'
import { getLineInterval, getMinMax } from '@/components/tdl/data/package/utils'
const app = useHostAppInstance()

const chartRef = ref(null);
const chartInstance = shallowRef(null);

// ================= 数据准备 =================
// 统一配置图表的主题颜色：青蓝、绿、黄、红 (严格对应你的图片)
const colors = ['#00d2ff'];


const drawLine = (xAxisData,yAxisData) => {
  const yMinMax = getMinMax(yAxisData)
  const lineInterval = getLineInterval(yMinMax)
  console.log('===drawLine:', yMinMax)
  // 线的数据
  const rawData = [
    { name: '每日波次批次', data: yAxisData },
  ];

  // 利用 map 动态生成 series，避免重复写大量相同配置
  const seriesConfig = rawData.map((item, index) => {
    return {
      name: item.name,
      type: 'line',
      smooth: true,        // 平滑曲线
      symbol: 'circle',    // 数据点为实心圆
      symbolSize: 8,       // 圆点大小
      data: item.data,
      itemStyle: {
        color: colors[index], // 分配专属颜色
        // 灵魂细节：给圆点加上和背景一样的边框，实现图里的“镂空隔离”效果
        borderColor: '#0b1426',
        borderWidth: 2
      },
      lineStyle: {
        width: 2 // 线条粗细
      }
    };
  });

  const option = {
    // 设置图表边距
    grid: {
      top: '15%',
      left: '3%',
      right: '10%',
      bottom: '5%',
      containLabel: true
    },
    // 💥 核心配置：Tooltip 提示框
    tooltip: {
      trigger: 'axis', // 坐标轴触发，即移到竖线上就显示4条数据
      backgroundColor: '#ffffff', // 白底
      borderColor: '#e8e8e8',
      textStyle: { color: '#333', fontSize: 13 },
      padding: [10, 15],
      // 提示框背景的阴影
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 6px;',
      // 鼠标放上去时的十字准星虚线
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#8a9ab9',
          type: 'dashed'
        }
      }
    },
    // X轴配置
    xAxis: {
      type: 'category',
      boundaryGap: false, // 贴边
      data: xAxisData,
      axisLine: {
        lineStyle: { color: '#2b3d5b' }
      },
      axisLabel: {
        color: '#8a9ab9',
        margin: 15 // 标签距离轴线的距离
      },
      axisTick: { show: false } // 隐藏刻度
    },
    // Y轴配置
    yAxis: {
      type: 'value',
      min: yMinMax.min,
      max: yMinMax.max,
      interval: lineInterval,
      axisLabel: {
        color: '#8a9ab9'
      },
      // 横向分割线 (虚化处理)
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(43, 61, 91, 0.4)',
          type: 'solid'
        }
      }
    },
    // 注入处理好的多条线的数据
    series: seriesConfig
  };

  chartInstance.value.setOption(option);
}
// ================= 响应式处理 =================
const handleResize = () => {
  if (chartInstance.value) chartInstance.value.resize();
};

// ================= 初始化图表 =================
const initChart = () => {
  chartInstance.value = echarts.init(chartRef.value);
};
const queryLineData = async (params) => {
  console.log('=====获取接口数据===')
  try {
    const resp = await app.request({
      api: '/sys/admin/statistics/wave-statistics/wave/daily-trend',
      params: params
    })
    // console.log('====doSearch resp:', JSON.stringify(resp))
    if (resp) {
      const xDatas = resp.dayList || []
      const yDatas = resp.valueList || []
      drawLine(xDatas,yDatas)
    }
  } catch (e) {

  }
}

defineExpose({
  queryLineData, initChart
})
onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (chartInstance.value) chartInstance.value.dispose();
});
</script>

<style scoped>
/* 最外层深蓝背景 */
.chart-container {
  width: 100%;
  height: 380px;
  background-color: #0b1426;
  border-radius: 8px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-family: 'Microsoft YaHei', sans-serif;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* 顶部信息条 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.left-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.right-title {
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #8a9ab9;
  font-size: 12px;
}
.line-view {
  height: 3px;
  width: 22px;
  margin-left: 5px;
  margin-right: 5px;
}

/* echarts 挂载节点 */
.echarts-box {
  flex: 1;
  width: 100%;
}
</style>
